/**
 * SystemStatusPanel - Status système complet pour admins
 * Affiche toutes les infos de santé (DB, OCR, mémoire, versions)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';

const SystemStatusPanel = ({ token }) => {
  const [health, setHealth] = useState(null);
  const [versions, setVersions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Calcul uptime frontend depuis window._env_.FRONTEND_STARTED_AT
  const getFrontendUptime = () => {
    const startedAt = window._env_?.FRONTEND_STARTED_AT;
    if (!startedAt) return null;

    const startTime = new Date(startedAt);
    if (isNaN(startTime.getTime())) return null;

    const now = new Date();
    const diffMs = now - startTime;

    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}j ${hours % 24}h ${minutes % 60}m`;
    if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const fetchData = useCallback(async () => {
    try {
      // Fetch health avec auth pour avoir les détails complets
      const [healthRes, versionsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/health/detailed`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/versions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (healthRes.ok) {
        const healthData = await healthRes.json();
        setHealth(healthData);
      }

      if (versionsRes.ok) {
        const versionsData = await versionsRes.json();
        if (versionsData.success) {
          setVersions(versionsData.data);
        }
      }

      setError(null);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'OK': case 'connected': return '#28a745';
      case 'DEGRADED': case 'timeout': return '#ffc107';
      case 'ERROR': case 'error': case 'unavailable': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const Card = ({ title, icon, children }) => (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      marginBottom: '16px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '16px',
        color: '#333',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <span>{icon}</span>{title}
      </h3>
      {children}
    </div>
  );

  const InfoRow = ({ label, value, highlight, color }) => (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0'
    }}>
      <span style={{ color: '#666', fontSize: '13px' }}>{label}</span>
      <span style={{
        fontWeight: highlight ? '600' : '400',
        color: color || (highlight ? '#333' : '#666'),
        fontFamily: 'monospace',
        fontSize: '13px'
      }}>
        {value || '-'}
      </span>
    </div>
  );

  const StatusBadge = ({ status }) => (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '600',
      color: 'white',
      background: getStatusColor(status)
    }}>
      {status}
    </span>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
        Chargement du statut système...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '12px',
        padding: '20px',
        textAlign: 'center',
        color: '#c53030'
      }}>
        <strong>Erreur de connexion</strong>
        <p style={{ margin: '8px 0 0' }}>{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header avec refresh */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div style={{ fontSize: '13px', color: '#666' }}>
          Dernière mise à jour : {lastUpdate?.toLocaleTimeString('fr-FR') || '-'}
          <span style={{ marginLeft: '8px', opacity: 0.6 }}>(auto-refresh 30s)</span>
        </div>
        <button
          onClick={fetchData}
          style={{
            padding: '8px 16px',
            background: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px'
          }}
        >
          Actualiser
        </button>
      </div>

      {/* Grille de cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '16px'
      }}>
        {/* Status Global */}
        <Card title="Statut Global" icon="🎯">
          {health && (
            <>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '16px'
              }}>
                <span style={{ fontSize: '40px' }}>
                  {health.status === 'OK' ? '✅' : health.status === 'DEGRADED' ? '⚠️' : '❌'}
                </span>
                <div>
                  <StatusBadge status={health.status} />
                  <p style={{ margin: '4px 0 0', color: '#666', fontSize: '14px' }}>
                    Version {health.version}
                  </p>
                </div>
              </div>
              <InfoRow label="Environnement" value={health.environment} />
              <InfoRow label="Build" value={health.buildDate && health.buildDate !== 'unknown' ? new Date(health.buildDate).toLocaleString('fr-FR') : 'Local'} />
              <InfoRow label="Uptime Backend" value={health.uptime} highlight />
              <InfoRow label="Uptime Frontend" value={getFrontendUptime() || '-'} highlight />
              <InfoRow label="Backend démarré" value={health.startedAt ? new Date(health.startedAt).toLocaleString('fr-FR') : '-'} />
              <InfoRow label="Frontend démarré" value={window._env_?.FRONTEND_STARTED_AT ? new Date(window._env_.FRONTEND_STARTED_AT).toLocaleString('fr-FR') : '-'} />
            </>
          )}
        </Card>

        {/* Base de données */}
        <Card title="Base de données" icon="🗄️">
          {health?.database ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>
                  {health.database.status === 'connected' ? '🟢' : '🔴'}
                </span>
                <StatusBadge status={health.database.status} />
              </div>
              <InfoRow label="Temps de réponse" value={`${health.database.responseTimeMs}ms`} highlight />
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Non disponible en production</p>
          )}
        </Card>

        {/* PaddleOCR */}
        <Card title="Service OCR (PaddleOCR)" icon="🔍">
          {health?.paddleocr ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px' }}>
                  {health.paddleocr.status === 'connected' ? '🟢' :
                   health.paddleocr.status === 'timeout' ? '🟡' : '🔴'}
                </span>
                <StatusBadge status={health.paddleocr.status} />
              </div>
              {health.paddleocr.status === 'connected' && (
                <>
                  <InfoRow label="Temps de réponse" value={`${health.paddleocr.responseTimeMs}ms`} highlight />
                  <InfoRow label="Version" value={health.paddleocr.version} />
                  <InfoRow label="URL" value={health.paddleocr.url} />
                </>
              )}
              {health.paddleocr.status === 'unavailable' && (
                <p style={{ color: '#dc3545', fontSize: '13px', margin: '8px 0 0' }}>
                  Conteneur non démarré
                </p>
              )}
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Non disponible en production</p>
          )}
        </Card>

        {/* WebSocket */}
        <Card title="Collaboration temps réel" icon="🔌">
          {health?.websocket ? (
            <>
              <InfoRow label="Utilisateurs connectés" value={health.websocket.activeUsers} highlight color="#28a745" />
              <InfoRow label="Verrous actifs" value={health.websocket.activeLocks} />
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Non disponible en production</p>
          )}
        </Card>

        {/* Mémoire */}
        <Card title="Ressources système" icon="💾">
          {health?.memory ? (
            <>
              <InfoRow label="Heap utilisé" value={health.memory.heapUsed} highlight />
              <InfoRow label="Heap total" value={health.memory.heapTotal} />
              <InfoRow label="RSS" value={health.memory.rss} />
              <InfoRow label="Système libre" value={health.memory.systemFree} color="#28a745" />
              <InfoRow label="Système total" value={health.memory.systemTotal} />
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Non disponible en production</p>
          )}
        </Card>

        {/* Infos serveur */}
        <Card title="Informations serveur" icon="🖥️">
          {health?.system ? (
            <>
              <InfoRow label="Node.js" value={health.system.nodeVersion} highlight />
              <InfoRow label="Plateforme" value={health.system.platform} />
              <InfoRow label="Architecture" value={health.system.arch} />
              <InfoRow label="CPUs" value={health.system.cpus} />
            </>
          ) : (
            <p style={{ color: '#999', fontStyle: 'italic' }}>Non disponible en production</p>
          )}
        </Card>
      </div>

      {/* Versions */}
      {versions && (
        <div style={{ marginTop: '24px' }}>
          <h2 style={{ fontSize: '18px', marginBottom: '16px', color: '#333' }}>
            Versions et Packages
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '16px'
          }}>
            {/* Versions Application */}
            <Card title="Application" icon="📦">
              <InfoRow label="Backend" value={versions.application?.backendVersion} highlight />
              <InfoRow label="Frontend" value={versions.application?.frontendVersion} highlight />
              <InfoRow label="Image Docker" value={versions.runtime?.imageTag} />
              <InfoRow label="Build" value={versions.runtime?.buildDate !== 'unknown' ? versions.runtime?.buildDate : 'Local'} />
            </Card>

            {/* Docker */}
            {versions.docker && (
              <Card title="Infrastructure Docker" icon="🐳">
                {['node', 'postgres', 'redis', 'nginx', 'paddleocr'].map((service) => {
                  const info = versions.docker[service];
                  if (!info || typeof info !== 'object') return null;
                  const labels = {
                    node: 'Node.js',
                    postgres: 'PostgreSQL',
                    redis: 'Redis',
                    nginx: 'Nginx',
                    paddleocr: 'PaddleOCR'
                  };
                  return (
                    <InfoRow
                      key={service}
                      label={labels[service]}
                      value={
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {info.currentVersion || info.current || 'N/A'}
                          {info.hasUpdate && (
                            <span style={{
                              background: '#dc3545',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '10px'
                            }}>
                              MAJ → {info.latestVersion}
                            </span>
                          )}
                          {!info.hasUpdate && info.latestVersion && (
                            <span style={{
                              background: '#28a745',
                              color: 'white',
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '10px'
                            }}>
                              OK
                            </span>
                          )}
                        </span>
                      }
                    />
                  );
                })}
                <InfoRow label="Environnement" value={versions.docker.environment} />
              </Card>
            )}

            {/* Monitoring */}
            {versions.monitoring && (
              <Card title="Stack Monitoring" icon="📊">
                <InfoRow label="Prometheus" value={versions.monitoring.prometheus || 'Non démarré'} highlight />
                <InfoRow label="Grafana" value={versions.monitoring.grafana || 'Non démarré'} highlight />
                <InfoRow label="Loki" value={versions.monitoring.loki || 'Non démarré'} />
                <InfoRow label="Alertmanager" value={versions.monitoring.alertmanager || 'Non démarré'} />
              </Card>
            )}

            {/* PaddleOCR Python */}
            {versions.paddleocr && (
              <Card title="PaddleOCR (Python)" icon="🐍">
                <InfoRow label="Service" value={versions.paddleocr.serviceVersion} highlight />
                <InfoRow label="Python" value={versions.paddleocr.pythonVersion} highlight />
                {versions.paddleocr.packages && Object.entries(versions.paddleocr.packages).map(([pkg, version]) => (
                  <InfoRow key={pkg} label={pkg} value={version} />
                ))}
              </Card>
            )}

            {/* Backend packages */}
            {versions.backend && (
              <Card title="Packages Backend" icon="⚙️">
                {Object.entries(versions.backend).slice(0, 10).map(([pkg, info]) => (
                  <InfoRow
                    key={pkg}
                    label={pkg}
                    value={typeof info === 'string' ? info : info.current}
                  />
                ))}
              </Card>
            )}

            {/* Frontend packages */}
            {versions.frontend && (
              <Card title="Packages Frontend" icon="🎨">
                {Object.entries(versions.frontend).slice(0, 10).map(([pkg, info]) => (
                  <InfoRow
                    key={pkg}
                    label={pkg}
                    value={typeof info === 'string' ? info : info.current}
                  />
                ))}
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemStatusPanel;
